import {
    ColumnDef,
    SelectionRange,
    SpreadsheetCell,
    SpreadsheetRowData,
} from "@components/spreadsheet/types";
import {
    SpreadsheetReference,
    SpreadsheetViewConfig,
} from "@common/schemas/spreadsheetReference.schema";
import { SpreadsheetRefContextValue } from "@components/tasks/SpreadsheetRefContext";
import { useSpreadsheetSelection } from "@components/spreadsheet/useSpreadsheetSelection";
import styles from "./ProfitLossActivationTaskBody.module.scss";

type ProfitLossRefContext = Pick<
    SpreadsheetRefContextValue,
    "registerFocusHandler"
>;

interface UseProfitLossSelectionParams {
    taskId: string;
    rows: SpreadsheetRowData[] | null;
    columns: ColumnDef[];
    expandedIds: string[];
    refCtx: ProfitLossRefContext | null;
    viewConfig?: SpreadsheetViewConfig;
    onPendingReferenceChange?: (ref: SpreadsheetReference | null) => void;
}

interface UseProfitLossSelectionResult {
    highlightedKeys: Set<string>;
    handleSelectionRangeChange: (
        cell: SpreadsheetCell | null,
        range: SelectionRange,
    ) => void;
    columnsWithHighlight: ColumnDef[];
    pendingReference: SpreadsheetReference | null;
    pendingCellCount: number;
}

/**
 * Thin wrapper around `useSpreadsheetSelection` that preserves the existing
 * public API consumed by `ProfitLossActivationTaskBody`.
 */
export function useProfitLossSelection({
    taskId,
    rows,
    columns,
    expandedIds,
    refCtx,
    viewConfig,
    onPendingReferenceChange,
}: UseProfitLossSelectionParams): UseProfitLossSelectionResult {
    const result = useSpreadsheetSelection({
        taskId,
        rows,
        columns,
        expandedIds,
        source: "profit_loss_activation",
        refCtx,
        highlightedCellClassName: styles.highlightedCell,
        viewConfig,
        onPendingReferenceChange,
    });

    return {
        // `highlightedKeys` is no longer in the shared hook's public API;
        // expose an empty set — callers only used it for debugging / tests.
        highlightedKeys: new Set<string>(),
        ...result,
    };
}
